import { plural, DECISION } from './decisions';

// ----------------------------------------------------------------------

/**
 * How many of these listings the seller's plan has to pay for.
 *
 * Only listings that are live on the marketplace count: the rest arrive as drafts, which the plan
 * does not limit. The server applies the same rule when it checks the selection, so counting any
 * other way would either block an import it would have accepted or promise one it will refuse.
 */
export const liveCount = (listings) => listings.filter((listing) => !listing?.isInactive).length;

/**
 * Everything the review needs to say what will happen and whether it fits.
 *
 * Linking costs nothing — the listing already exists, so only the marketplace post is new. Leaving
 * an item out costs nothing either. What the plan pays for is a brand-new live listing: an item the
 * seller decided is different after all, and each of the other listings they picked. Drafts are
 * free on both counts.
 *
 * @param {object} params
 * @param {Array<{key: string, isInactive?: boolean}>} params.items one per item under review
 * @param {object} params.decisions what the seller has changed, keyed by item; anything they have
 *   not touched counts as `defaultChoice`
 * @param {string} params.defaultChoice
 * @param {Array<object>} params.pickedOthers the other listings that will come in
 * @param {number} params.remainingSlots room left on the plan
 * @returns {{link: number, newItem: number, newItemsLive: number, leaveOut: number, others: number,
 *   planSpend: number, roomLeft: number, overBy: number}}
 */
export function reviewTotals ({
  items = [],
  decisions = {},
  defaultChoice,
  pickedOthers = [],
  remainingSlots = Infinity,
}) {
  const decided = items.map((item) => ({ ...item, choice: decisions[item.key] ?? defaultChoice }));
  const count = (decision) => decided.filter((item) => item.choice === decision).length;

  const newItem = count(DECISION.NEW_ITEM);
  const newItemsLive = liveCount(decided.filter((item) => item.choice === DECISION.NEW_ITEM));
  const planSpend = newItemsLive + liveCount(pickedOthers);

  return {
    link: count(DECISION.LINK),
    newItem,
    newItemsLive,
    leaveOut: count(DECISION.LEAVE_OUT),
    others: pickedOthers.length,
    planSpend,
    roomLeft: Math.max(0, remainingSlots - planSpend),
    overBy: Math.max(0, planSpend - remainingSlots),
  };
}

/**
 * The listings a seller gets when they take the offer to fill the room they have left: every draft,
 * because the plan does not charge for those, and as many live listings as the room allows.
 *
 * @param {Array<{listing: object}>} rows in the order the seller sees them
 * @param {number} liveRoom how many live listings the plan still has room for
 */
export function rowsThatFit (rows, liveRoom) {
  let room = Math.max(0, liveRoom);

  return rows.filter(({ listing }) => {
    if (listing?.isInactive) return true;
    if (room <= 0) return false;
    room -= 1;
    return true;
  });
}

/**
 * What to tell a seller who has gone over their plan — and only ever something they can act on from
 * where they are standing.
 *
 * Taking an item out of the pick-list only helps if they picked something. When the overspend is
 * entirely their own "bring it in as a new item" choices — which is the normal case, because the
 * rest of the import came in before the review even opened — there is nothing on the screen to take
 * out, and naming one sends them hunting for a control that is not there. Linking those items after
 * all is the way forward, so that is what it says.
 *
 * @param {{overBy: number, planSpend: number, newItemsLive: number}} totals from reviewTotals
 * @returns {string|null} null when the seller is within their plan
 */
export function overspendAdvice ({ overBy, planSpend, newItemsLive }) {
  if (!overBy) return null;

  if (planSpend > newItemsLive) {
    return `Take ${plural(overBy, 'item')} out to fit your plan.`;
  }

  return `Link ${overBy === 1 ? 'it' : `${overBy} of them`} instead, or free up space on your plan.`;
}

/**
 * What the seller is about to do, read back to them in their own words — only the parts that are
 * actually happening, so a plain "link everything" import says one short thing.
 */
export function recapLine ({ link, newItem, leaveOut, others }) {
  const parts = [];
  if (link) parts.push(`${link} linked`);
  if (newItem) parts.push(plural(newItem, 'new item'));
  if (leaveOut) parts.push(`${leaveOut} left out`);
  if (others) parts.push(`${others} more coming in`);
  return parts.join(' · ');
}
